/* eslint-disable react/no-multi-comp */
import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { CustomPopover } from 'components';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { ListActionsIcon, VerticalListActionsIcon } from 'assets/icons';
import { getInitials } from 'utils/commonUtils';
import DateUtils from 'utils/dateUtils';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { EstimateMoreActions, EstimateStatus } from '../constants';
import { EstimateListItem } from '../estimates.model';
import {
  AddInvoiceAction,
  AddJobAction,
  ArchiveEstimateAction,
  DeleteEstimateAction,
  DuplicateEstimateAction,
  MarkAsAcceptedAction,
  ShareEstimateAction,
  ViewInvoiceAction,
  ViewJobAction
} from './EstimateActions';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { SubscriptionStatus } from 'containers/app/app.model';
import { getExpiryTextForEstimate, getStatusColor } from '../utils';

interface EstimatesTableProps {
  estimatesList: EstimateListItem[];
  onEstimateClick: (estimateId: string) => void;
  currentSelectedId: string;
  handleMoreActionsClick: (type: string, estimate: EstimateListItem) => void;
}

const EstimatesTable: FC<EstimatesTableProps> = ({
  estimatesList,
  onEstimateClick,
  currentSelectedId,
  handleMoreActionsClick
}) => {
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const [currentActionsPopoverId, setCurrentActionsPopoverId] = useState<string | null>(null);

  const canCreateEstimates = currentTeam?.subscriptionStatus !== SubscriptionStatus.SUBSCRIPTION_EXPIRED;

  const tableHeaders = [
    'Estimate ID',
    'Customer',
    'Created date',
    'Sold by',
    'Status',
    'Amount',
    'Related jobs / invoices',
    'Created by'
  ];

  const ListActionsAnchor: React.FC<{ isPanelOpen: boolean }> = ({ isPanelOpen }) => (
    <div id="estimate-table-more-options" className="py-2 sm:pl-4">
      <ListActionsIcon
        className={twMerge(
          'hidden shrink-0 lg:flex',
          classnames({
            'fill-current text-secondary': isPanelOpen
          })
        )}
      />
      <VerticalListActionsIcon
        className={twMerge(
          'mx-2 flex shrink-0 lg:hidden',
          classnames({
            'fill-current text-secondary': isPanelOpen
          })
        )}
      />
    </div>
  );

  const PendingEstimateActions: FC<{ estimate: EstimateListItem }> = ({ estimate }) => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      <ShareEstimateAction
        onShareEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.SHARE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
      <MarkAsAcceptedAction
        onMarkAsAccepted={() => {
          handleMoreActionsClick(EstimateMoreActions.MARK_AS_ACCEPTED, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
      {canCreateEstimates && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            handleMoreActionsClick(EstimateMoreActions.DUPLICATE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ArchiveEstimateAction
        onArchiveEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.ARCHIVE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
      <DeleteEstimateAction
        onDeleteEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.DELETE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
    </div>
  );

  const AcceptedEstimateActions: FC<{ estimate: EstimateListItem }> = ({ estimate }) => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      <ShareEstimateAction
        onShareEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.SHARE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
      {canCreateEstimates && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            handleMoreActionsClick(EstimateMoreActions.DUPLICATE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ArchiveEstimateAction
        onArchiveEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.ARCHIVE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
      <DeleteEstimateAction
        onDeleteEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.DELETE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
    </div>
  );

  const RejectedEstimateActions: FC<{ estimate: EstimateListItem }> = ({ estimate }) => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      {canCreateEstimates && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            handleMoreActionsClick(EstimateMoreActions.DUPLICATE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ArchiveEstimateAction
        onArchiveEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.ARCHIVE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
      <DeleteEstimateAction
        onDeleteEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.DELETE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
    </div>
  );

  const ArchivedEstimateActions: FC<{ estimate: EstimateListItem }> = ({ estimate }) => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      {canCreateEstimates && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            handleMoreActionsClick(EstimateMoreActions.DUPLICATE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ShareEstimateAction
        onShareEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.SHARE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
      <DeleteEstimateAction
        onDeleteEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.DELETE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
    </div>
  );

  const InvoicedEstimateActions: FC<{ estimate: EstimateListItem }> = ({ estimate }) => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      {estimate.relatedEntities?.invoice ? (
        <ViewInvoiceAction
          onViewInvoice={() => {
            handleMoreActionsClick(EstimateMoreActions.VIEW_INVOICE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      ) : (
        <AddInvoiceAction
          onAddInvoice={() => {
            handleMoreActionsClick(EstimateMoreActions.ADD_INVOICE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      {estimate.relatedEntities?.job ? (
        <ViewJobAction
          onViewJob={() => {
            handleMoreActionsClick(EstimateMoreActions.VIEW_JOB, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      ) : (
        <AddJobAction
          onAddJob={() => {
            handleMoreActionsClick(EstimateMoreActions.ADD_JOB, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <ShareEstimateAction
        onShareEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.SHARE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
      {canCreateEstimates && (
        <DuplicateEstimateAction
          onDuplicateEstimate={() => {
            handleMoreActionsClick(EstimateMoreActions.DUPLICATE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      {!estimate.relatedEntities?.invoice && (
        <ArchiveEstimateAction
          onArchiveEstimate={() => {
            handleMoreActionsClick(EstimateMoreActions.ARCHIVE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      {!estimate.relatedEntities?.invoice && (
        <DeleteEstimateAction
          onDeleteEstimate={() => {
            handleMoreActionsClick(EstimateMoreActions.DELETE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
    </div>
  );

  const ExpiredEstimateActions: FC<{ estimate: EstimateListItem }> = ({ estimate }) => (
    <div className="space-y-3 text-sbase font-semibold text-primaryText">
      {estimate.relatedEntities?.invoice ? (
        <ViewInvoiceAction
          onViewInvoice={() => {
            handleMoreActionsClick(EstimateMoreActions.VIEW_INVOICE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      ) : (
        <AddInvoiceAction
          onAddInvoice={() => {
            handleMoreActionsClick(EstimateMoreActions.ADD_INVOICE, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      {estimate.relatedEntities?.job ? (
        <ViewJobAction
          onViewJob={() => {
            handleMoreActionsClick(EstimateMoreActions.VIEW_JOB, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      ) : (
        <AddJobAction
          onAddJob={() => {
            handleMoreActionsClick(EstimateMoreActions.ADD_JOB, estimate);
            setCurrentActionsPopoverId(null);
          }}
        />
      )}
      <DeleteEstimateAction
        onDeleteEstimate={() => {
          handleMoreActionsClick(EstimateMoreActions.DELETE, estimate);
          setCurrentActionsPopoverId(null);
        }}
      />
    </div>
  );

  const renderEstimateActions = (status: EstimateStatus, estimate: EstimateListItem) => {
    switch (status) {
      case EstimateStatus.PENDING:
        return <PendingEstimateActions estimate={estimate} />;
      case EstimateStatus.ACCEPTED:
        return <AcceptedEstimateActions estimate={estimate} />;
      case EstimateStatus.REJECTED:
        return <RejectedEstimateActions estimate={estimate} />;
      case EstimateStatus.ARCHIVED:
        return <ArchivedEstimateActions estimate={estimate} />;
      case EstimateStatus.INVOICED:
        return <InvoicedEstimateActions estimate={estimate} />;
      case EstimateStatus.EXPIRED:
        return <ExpiredEstimateActions estimate={estimate} />;
    }
  };

  const onToggleListItemActions = (estimateId: string) => {
    setCurrentActionsPopoverId(prevId => (prevId === estimateId ? null : estimateId));
  };

  const handleClosePopover = (estimateId: string) => {
    if (currentActionsPopoverId === estimateId) setCurrentActionsPopoverId(null);
  };

  const handleInvoiceClick = (invoiceId: string, estimateId: string) => {
    navigate(`/estimates/${currentTeam.id}/${estimateId}/invoice/${invoiceId}`);
  };

  const handleJobClick = (jobId: string, estimateId: string) => {
    navigate(`/estimates/${currentTeam.id}/${estimateId}/jobs/${jobId}`);
  };

  return (
    <table className="relative w-full table-auto">
      <thead className="border-borderGray bg-primary">
        <tr
          className="text-slateGrey mx-[30px] border-b-[0.5px] border-secondaryBtn text-left text-xs 
        lg:text-base [&>*]:py-5">
          {tableHeaders.map((header, index) => (
            <th
              key={index}
              className={`min-w-[100px] max-w-[120px] text-sbase font-semibold text-primaryText ${
                index === 0 ? 'pl-[30px]' : ''
              }`}>
              {header}
            </th>
          ))}
          <th></th>
        </tr>
      </thead>
      <tbody>
        {estimatesList &&
          estimatesList.map(estimate => {
            const remainingMemberCount = estimate.assignees?.length - 3;
            return (
              <tr
                key={estimate.id}
                className={`remove-highlight cursor-pointer border-b border-secondaryBtn align-top [&>*]:py-5 [&>*]:pr-2
                ${currentSelectedId === estimate.id ? 'bg-secondaryBtn' : ''}`}
                onClick={() => onEstimateClick(estimate.id)}>
                <td className="max-w-[120px] !pl-[30px]">
                  <div className="truncate text-sbase text-primaryText">{estimate.estimateNumber}</div>
                </td>
                <td className="max-w-[120px]">
                  <div className="truncate text-sbase text-primaryText">{estimate.customerName}</div>
                </td>
                <td className="max-w-[120px]">
                  <div className="truncate text-sbase text-primaryText">
                    {DateUtils.getDateInFormat({ date: new Date(estimate.createdAt), dateFormat: 'dd MMM yyyy' })}
                  </div>
                </td>
                <td className="min-w-[80px] max-w-[120px]">
                  <div className="flex flex-col gap-1">
                    {estimate.assignees?.length > 0 ? (
                      estimate.assignees.map(
                        (member, index) =>
                          index < 3 && (
                            <div key={member.id} className="text-sbase text-primaryText ">
                              <span>{member.name}</span>
                              <span className="pl-2 font-semibold text-primaryBtn">
                                {index === 2 && remainingMemberCount > 0 ? `+${remainingMemberCount}` : ''}
                              </span>
                            </div>
                          )
                      )
                    ) : (
                      <div className="min-w-[100px] max-w-[120px] text-sbase text-primaryText">-</div>
                    )}
                  </div>
                </td>
                <td className="min-w-[100px] max-w-[120px]">
                  <div
                    className={'text-sbase font-bold capitalize'}
                    style={{
                      color: getStatusColor(estimate.status as EstimateStatus)
                    }}>
                    {estimate.status.toLowerCase()}
                    {estimate.status == EstimateStatus.PENDING ? (
                      <div className="mt-1 text-px13 font-normal normal-case text-accentText">
                        {getExpiryTextForEstimate(estimate.expiresAt)}
                      </div>
                    ) : (
                      <div className="mt-1 text-px13 font-normal normal-case text-accentText">
                        Updated on{' '}
                        {estimate.updatedAt
                          ? DateUtils.getDateInFormat({ date: new Date(estimate.updatedAt), dateFormat: 'dd MMM yyyy' })
                          : ''}
                      </div>
                    )}
                  </div>
                </td>
                <td className="min-w-[80px] max-w-[120px]">
                  <div className="truncate text-sbase font-semibold text-primaryText">
                    {getAmountWithCurrency(estimate.amount)}
                  </div>
                </td>
                <td className="min-w-[160px] max-w-[170px]">
                  {estimate.relatedEntities?.invoice || estimate.relatedEntities?.job ? (
                    <div className="flex flex-col gap-2 text-sbase font-semibold text-secondaryBtnText">
                      {estimate.relatedEntities?.invoice && (
                        <p
                          className="cursor-pointer hover:text-primaryBtn"
                          onClick={e => {
                            e.stopPropagation();
                            handleInvoiceClick(estimate.relatedEntities.invoice.entityId, estimate.id);
                          }}>
                          {estimate.relatedEntities.invoice.entityNumber}
                        </p>
                      )}
                      {estimate.relatedEntities?.job && (
                        <p
                          className="cursor-pointer hover:text-primaryBtn"
                          onClick={e => {
                            e.stopPropagation();
                            handleJobClick(estimate.relatedEntities.job.entityId, estimate.id);
                          }}>
                          {estimate.relatedEntities.job.entityNumber}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="min-w-[100px] max-w-[120px] text-sbase text-primaryText">-</div>
                  )}
                </td>
                <td className="min-w-[80px] max-w-[120px]">
                  <div className="flex flex-row items-center gap-2">
                    <CustomAvatar height="h-6" width="w-6" src={estimate.merchantProfilePicture}>
                      <div className="truncate text-px13 font-semibold text-[#333333]">
                        {getInitials(estimate.merchantName)}
                      </div>
                    </CustomAvatar>
                    <div className="truncate text-sbase text-primaryText lg:inline-block">{estimate.merchantName}</div>
                  </div>
                </td>
                <td className="w-14 !pr-[30px]">
                  <CustomPopover
                    showArrow={true}
                    arrowOffset="right-6"
                    highlightAnchor={false}
                    anchorComponent={ListActionsAnchor}
                    anchorComponentId={`${estimate.id}-more-options`}
                    zIndex="z-[101]"
                    offset="-right-6"
                    margin="mt-1"
                    onClosePopover={() => handleClosePopover(estimate.id)}
                    show={currentActionsPopoverId === estimate.id}
                    onTogglePopover={() => onToggleListItemActions(estimate.id)}>
                    {renderEstimateActions(estimate.status as EstimateStatus, estimate)}
                  </CustomPopover>
                </td>
              </tr>
            );
          })}
      </tbody>
    </table>
  );
};

export default EstimatesTable;
